import { memo, useMemo } from 'react'
import type { Groups, Relation, Table } from '@/types'
import { useSettingsStore, useSelectionStore } from '@/stores'
import { getTableHeight, getTableWidth } from '@/utils/geometry'
import { getRelationshipColor } from '@/utils/relationships'
import { RelationPath } from '@/components/Relation/RelationPath'

interface RelationsLayerProps {
  relationships: Relation[]
  tables: Table[]
  groups: Groups
  readOnly?: boolean
}

export const RelationsLayer = memo(function RelationsLayer({
  relationships,
  tables,
  groups,
  readOnly = false,
}: RelationsLayerProps) {
  const selectedRelationshipId = useSelectionStore((state) => state.selectedRelationshipId)
  const selectRelationship = useSelectionStore((state) => state.selectRelationship)
  const animationEnabled = useSettingsStore((state) => state.relationshipAnimationEnabled)
  const theme = useSettingsStore((state) => state.resolvedTheme)

  const tablesById = useMemo(
    () => new Map(tables.map((table) => [table.id, table])),
    [tables],
  )

  const canvasSize = useMemo(() => {
    const maxX = tables.reduce((value, table) => Math.max(value, table.x + getTableWidth(table) + 200), 2000)
    const maxY = tables.reduce((value, table) => Math.max(value, table.y + getTableHeight(table) + 200), 2000)
    return { width: maxX, height: maxY }
  }, [tables])

  return (
    <svg
      id="svgl"
      width={canvasSize.width}
      height={canvasSize.height}
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'visible',
      }}
    >
      {relationships.map((relationship) => {
        const fromTable = tablesById.get(relationship.from)
        const toTable = tablesById.get(relationship.to)

        if (!fromTable || !toTable) {
          return null
        }

        return (
          <RelationPath
            key={relationship.id}
            relation={relationship}
            fromTable={fromTable}
            toTable={toTable}
            allTables={tables}
            color={getRelationshipColor(relationship, tables, groups)}
            isSelected={selectedRelationshipId === relationship.id}
            animationEnabled={animationEnabled}
            theme={theme}
            onSelect={selectRelationship}
            readOnly={readOnly}
          />
        )
      })}
    </svg>
  )
})
